/**
 * AdminTenantDomains — CORS allow-list manager (super-admin only).
 *
 * Add/remove/enable the domains browsers may use to call the ecosystem
 * `/api/*` endpoints. Backed by the `tenant_domains` Mongo collection
 * consumed by `core.cors_middleware.TenantCORSMiddleware`. Preview and
 * localhost origins stay allowed via always-allow regex patterns.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Globe2, Plus, Trash2, ShieldCheck } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const EMPTY = { tenant_id: '', domain: '', label: '' };

export default function AdminTenantDomains() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(EMPTY);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/tenant-domains`, { headers: authH() });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.tenant_id.trim() || !draft.domain.trim()) {
      toast.error('Tenant slug + domain are required'); return;
    }
    try {
      await axios.post(`${API}/api/admin/tenant-domains`, draft, { headers: authH() });
      toast.success('Domain added — active within 60s');
      setDraft(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  const toggle = async (row) => {
    try {
      await axios.put(`${API}/api/admin/tenant-domains/${row.id}`,
                       { is_active: !row.is_active }, { headers: authH() });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async (row) => {
    if (!confirm(`Remove ${row.domain}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/tenant-domains/${row.id}`, { headers: authH() });
      toast.success('Removed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const byTenant = rows.reduce((acc, r) => {
    (acc[r.tenant_id] ||= []).push(r); return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6" data-testid="admin-tenant-domains-page">
      <div className="max-w-4xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-emerald-300" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">Tenant CORS Allow-List</h1>
            <p className="text-sm text-slate-400">
              Domains that may call the ecosystem <code>/api/*</code> endpoints from a browser.
              Preview and localhost are always allowed via regex patterns —
              add production domains here before going live.
            </p>
          </div>
        </header>

        <Card className="bg-slate-900/60 border-emerald-500/30">
          <CardHeader>
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-300" /> Add a domain
            </CardTitle>
            <CardDescription className="text-slate-400">
              Include scheme (<code>https://</code>). Add each hostname variant separately
              (bare + www) if both are used.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-400">Tenant slug</Label>
              <Input value={draft.tenant_id}
                     onChange={(e) => setDraft({ ...draft, tenant_id: e.target.value })}
                     className="bg-slate-900 border-slate-800 mt-1"
                     placeholder="abmhc, fhbf, church, …"
                     data-testid="td-add-tenant" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-400">Domain (scheme://host)</Label>
              <Input value={draft.domain}
                     onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                     className="bg-slate-900 border-slate-800 mt-1"
                     placeholder="https://abmhc.org"
                     data-testid="td-add-domain" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Label (optional)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={draft.label}
                       onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                       className="bg-slate-900 border-slate-800 flex-1"
                       placeholder="e.g. one.com prod"
                       data-testid="td-add-label" />
                <Button onClick={add} data-testid="td-add-btn">Add</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {Object.keys(byTenant).length === 0 && !busy && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="py-8 text-center text-sm text-slate-400">
              <Globe2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No domains configured yet.
            </CardContent>
          </Card>
        )}

        {Object.entries(byTenant).map(([tenantId, list]) => (
          <Card key={tenantId} className="bg-slate-900/60 border-slate-800"
                data-testid={`td-tenant-${tenantId}`}>
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Globe2 className="h-4 w-4 text-slate-400" />
                <code className="text-emerald-300">{tenantId}</code>
                <span className="text-xs text-slate-500">({list.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {list.map((row) => (
                <div key={row.id}
                     className="flex items-center gap-3 border border-slate-800 rounded p-2 bg-slate-800/20"
                     data-testid={`td-row-${row.id}`}>
                  <Switch checked={row.is_active}
                          onCheckedChange={() => toggle(row)}
                          data-testid={`td-toggle-${row.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono truncate">{row.domain}</div>
                    {row.label && (
                      <div className="text-xs text-slate-500 truncate">{row.label}</div>
                    )}
                  </div>
                  {row.is_active
                    ? <Badge variant="outline" className="border-emerald-700 text-emerald-300 text-[10px]">active</Badge>
                    : <Badge variant="outline" className="border-slate-700 text-slate-500 text-[10px]">off</Badge>}
                  <Button variant="outline" size="sm"
                          className="border-rose-800 text-rose-300 hover:bg-rose-500/10"
                          onClick={() => remove(row)}
                          data-testid={`td-del-${row.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
